import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { useAuth } from "./auth";
import PWAUpdateManager from "./components/PWAUpdateManager";

// Lazy load route components
const LoginPage = lazy(() => import("./Routes/LoginPage"));
const HubLoginPage = lazy(() => import("./Routes/HubLoginPage"));
const AdminPage = lazy(() => import("./Routes/AdminPage"));
const UserInbox = lazy(() => import("./components/user/UserInbox"));
const UserPage = lazy(() => import("./Routes/UserPage"));
const UserOpenTickets = lazy(() => import("./components/user/UserOpenTickets"));
const UserResolvedTickets = lazy(() => import("./components/user/UserResolvedTickets"));
const ManagerPage = lazy(() => import("./Routes/ManagerPage"));
const ManagerUsersPage = lazy(() => import("./components/manager/ManagerUsersPage"));
const ManagerSubjectsPage = lazy(() => import("./components/manager/ManagerSubjectsPage"));
const ManagerResolvedTickets = lazy(() => import("./components/manager/ManagerResolvedTickets"));
const AdminOverView = lazy(() => import("./components/admin/AdminOverView"));
const AdminReports = lazy(() => import("./components/admin/AdminReports"));
const ManagerCreatedTickets = lazy(() => import("./components/manager/ManagerCreatedTickets"));
const ManagerInbox = lazy(() => import("./components/manager/ManagerInbox"));
const AdminUsersPage = lazy(() => import("./components/admin/AdminUsersPage"));
const AdminTickets = lazy(() => import("./components/admin/AdminTickets"));
const AdminAIDashboard = lazy(() => import("./components/admin/AdminAIDashboard"));
const AdminCategoriesPage = lazy(() => import("./components/admin/AdminCategoriesPage"));
const GMReports = lazy(() => import("./components/gm/GMReports"));
const GMOverView = lazy(() => import("./components/gm/GMOverView"));
const SupervisorOverView = lazy(() => import("./components/supervisor/SupervisorOverView"));
const SupervisorReports = lazy(() => import("./components/supervisor/SupervisorReports"));
const SettingsPage = lazy(() => import("./Routes/SettingsPage"));
const UserGuidePage = lazy(() => import("./Routes/UserGuidePage"));

// Global Loading Page for Suspense
const SectionLoading = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="flex flex-col items-center">
      <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
      <p className="mt-4 text-sm font-medium text-gray-500">Loading Section...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ element, roles }) => {
  const { token, userRole } = useAuth();
  if (!token) {
    return <Navigate to="/" />;
  }
  if (roles && !roles.includes(userRole)) {
    return <Navigate to="/" />;
  }
  return element;
};

const AuthenticatedRoute = () => {
  const { token, userRole } = useAuth();

  // If user is already authenticated, redirect to their dashboard
  if (token && userRole) {
    if (userRole === "admin") {
      return <Navigate to="/admin" replace />;
    } else if (userRole === "manager") {
      return <Navigate to="/manager" replace />;
    } else if (userRole === "gm") {
      return <Navigate to="/gm" replace />;
    } else if (userRole === "supervisor") {
      return <Navigate to="/supervisor" replace />;
    } else {
      return <Navigate to="/user" replace />;
    }
  }

  // If not authenticated, show login page
  return <LoginPage />;
};

const App = () => {
  return (
    <Router>
      <Suspense fallback={<SectionLoading />}>
        <Routes>
          <Route path="/" element={<AuthenticatedRoute />} />
          <Route path="/hub-login" element={<HubLoginPage />} />
          <Route
            path="/admin"
            element={<ProtectedRoute element={<AdminPage />} roles={["admin"]} />}
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute element={<AdminUsersPage />} roles={["admin"]} />
            }
          />
          <Route
            path="/admin/overview"
            element={
              <ProtectedRoute element={<AdminOverView />} roles={["admin"]} />
            }
          />
          <Route
            path="/admin/tickets"
            element={
              <ProtectedRoute element={<AdminTickets />} roles={["admin"]} />
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute element={<AdminReports />} roles={["admin"]} />
            }
          />
          <Route
            path="/admin/ai"
            element={
              <ProtectedRoute element={<AdminAIDashboard />} roles={["admin"]} />
            }
          />
          <Route
            path="/admin/categories"
            element={
              <ProtectedRoute element={<AdminCategoriesPage />} roles={["admin"]} />
            }
          />

          <Route
            path="/manager"
            element={
              <ProtectedRoute element={<ManagerPage />} roles={["manager"]} />
            }
          />
          <Route
            path="/manager/inbox"
            element={
              <ProtectedRoute element={<ManagerInbox />} roles={["manager"]} />
            }
          />
          <Route
            path="/manager/created"
            element={
              <ProtectedRoute
                element={<ManagerCreatedTickets />}
                roles={["manager"]}
              />
            }
          />
          <Route
            path="/manager/users"
            element={
              <ProtectedRoute
                element={<ManagerUsersPage />}
                roles={["manager"]}
              />
            }
          />
          <Route
            path="/manager/subjects"
            element={
              <ProtectedRoute
                element={<ManagerSubjectsPage />}
                roles={["manager"]}
              />
            }
          />
          <Route
            path="/manager/resolved"
            element={
              <ProtectedRoute
                element={<ManagerResolvedTickets />}
                roles={["manager"]}
              />
            }
          />

          {/* General Manager Routes */}
          <Route
            path="/gm"
            element={
              <ProtectedRoute element={<ManagerPage />} roles={["gm"]} />
            }
          />
          <Route
            path="/gm/overview"
            element={
              <ProtectedRoute element={<GMOverView />} roles={["gm"]} />
            }
          />
          <Route
            path="/gm/tickets"
            element={
              <ProtectedRoute element={<AdminTickets />} roles={["gm"]} />
            }
          />
          <Route
            path="/gm/reports"
            element={
              <ProtectedRoute element={<GMReports />} roles={["gm"]} />
            }
          />

          {/* Supervisor Routes */}
          <Route
            path="/supervisor"
            element={
              <ProtectedRoute element={<ManagerPage />} roles={["supervisor"]} />
            }
          />
          <Route
            path="/supervisor/overview"
            element={
              <ProtectedRoute element={<SupervisorOverView />} roles={["supervisor"]} />
            }
          />
          <Route
            path="/supervisor/tickets"
            element={
              <ProtectedRoute element={<AdminTickets />} roles={["supervisor"]} />
            }
          />
          <Route
            path="/supervisor/reports"
            element={
              <ProtectedRoute element={<SupervisorReports />} roles={["supervisor"]} />
            }
          />

          <Route
            path="/user"
            element={<ProtectedRoute element={<UserPage />} roles={["user"]} />}
          />
          <Route
            path="/user/inbox"
            element={<ProtectedRoute element={<UserInbox />} roles={["user"]} />}
          />
          <Route
            path="/user/opentickets"
            element={
              <ProtectedRoute element={<UserOpenTickets />} roles={["user"]} />
            }
          />
          <Route
            path="/user/resolved"
            element={
              <ProtectedRoute element={<UserResolvedTickets />} roles={["user"]} />
            }
          />

          {/* Settings routes for all user types */}
          <Route
            path="/settings"
            element={<ProtectedRoute element={<SettingsPage />} roles={["user", "manager", "admin", "gm", "supervisor"]} />}
          />
          <Route
            path="/settings/user-guide"
            element={<ProtectedRoute element={<UserGuidePage />} roles={["user", "manager", "admin", "gm", "supervisor"]} />}
          />

        </Routes>
      </Suspense>

      {/* PWA Update Manager */}
      <PWAUpdateManager />
    </Router>
  );
};

export default App;
